import type { MouseEvent as ReactMouseEvent } from 'react';
import type { DraggableEvent } from 'react-draggable';
import { API_BASE_URL } from 'api/apiClient';

export type ObservationId = string | number;
type ObservationAsset = 'image' | 'thumb' | 'state';

export interface ObservationRecord {
  id: ObservationId;
  title?: string;
  description?: string | null;
  index?: number;
  deletedAt?: string | Date | null;
  createdAt?: string | Date;
  updatedAt?: string | Date;
  gridId?: ObservationId;
  grid_id?: ObservationId;
  reportId?: ObservationId;
  imageId?: ObservationId;
  stateId?: ObservationId;
  imageUrl?: string;
  thumbnailUrl?: string;
  stateUrl?: string;
}

export interface ObservationReport {
  id: ObservationId;
  name?: string;
  gridId?: ObservationId;
  grid_id?: ObservationId;
}

export interface SlotPosition {
  left: number;
  top: number;
}

export interface DragState {
  id: string;
  x: number;
  y: number;
}

export interface PointerPosition {
  x: number;
  y: number;
}

export const DRAG_MIN_DISTANCE = 3;
export const AUTO_SCROLL_THRESHOLD_PX = 110;
export const AUTO_SCROLL_MAX_SPEED_PX = 26;

const buildProjectObservationAssetUrl = (
  projectId: ObservationId,
  reportId: ObservationId,
  observationId: ObservationId,
  asset: ObservationAsset
) =>
  `${API_BASE_URL}/api/projects/${encodeURIComponent(projectId)}/reports/${encodeURIComponent(reportId)}/observations/${encodeURIComponent(observationId)}/${asset}`;

export const buildProjectObservationImageUrl = (
  projectId: ObservationId,
  reportId: ObservationId,
  observationId: ObservationId
) => buildProjectObservationAssetUrl(projectId, reportId, observationId, 'image');

export const buildProjectObservationThumbnailUrl = (
  projectId: ObservationId,
  reportId: ObservationId,
  observationId: ObservationId
) => buildProjectObservationAssetUrl(projectId, reportId, observationId, 'thumb');

export const buildProjectObservationStateUrl = (
  projectId: ObservationId,
  reportId: ObservationId,
  observationId: ObservationId
) => buildProjectObservationAssetUrl(projectId, reportId, observationId, 'state');

const isMouseEvent = (event: DraggableEvent): event is MouseEvent => 'clientY' in event;
const isTouchEvent = (event: DraggableEvent): event is TouchEvent => 'touches' in event;

export const getPointerClientY = (event: DraggableEvent): number | null => {
  if (isMouseEvent(event)) return event.clientY;
  return isTouchEvent(event) && event.touches.length > 0 ? (event.touches[0]?.clientY ?? null) : null;
};

export const getPointerClientX = (event: DraggableEvent): number | null => {
  if (isMouseEvent(event)) return event.clientX;
  return isTouchEvent(event) && event.touches.length > 0 ? (event.touches[0]?.clientX ?? null) : null;
};

const normalizeCustomIndex = (value: number | undefined) => {
  const numericValue = Number(value);
  return !Number.isFinite(numericValue) || numericValue === -1 ? Number.MAX_SAFE_INTEGER : numericValue;
};

export const compareRecordsByCustomIndex = (left: ObservationRecord, right: ObservationRecord) => {
  const difference = normalizeCustomIndex(left.index) - normalizeCustomIndex(right.index);
  return difference || (left.title ?? '').localeCompare(right.title ?? '');
};

export const isDeletedObservation = (record: ObservationRecord) => Boolean(record.deletedAt);

export const orderObservationRecords = (records: ObservationRecord[], sortBy: 'custom' | 'date') => {
  const compare =
    sortBy === 'date'
      ? (left: ObservationRecord, right: ObservationRecord) =>
          new Date(left.createdAt ?? 0).getTime() - new Date(right.createdAt ?? 0).getTime()
      : compareRecordsByCustomIndex;

  return [
    ...records.filter((record) => !isDeletedObservation(record)).sort(compare),
    ...records.filter(isDeletedObservation).sort(compare),
  ];
};

export const reorderIds = (ids: string[], draggedId: string, targetIndex: number) => {
  const nextIds = ids.filter((id) => String(id) !== String(draggedId));
  nextIds.splice(Math.max(0, Math.min(targetIndex, nextIds.length)), 0, draggedId);
  return nextIds;
};

export const reorderIdsGroup = (ids: string[], draggedIds: string[], targetIndex: number) => {
  if (draggedIds.length === 0) return ids;
  if (draggedIds.length === 1) return reorderIds(ids, draggedIds[0], targetIndex);
  const draggedIdSet = new Set(draggedIds);
  const remainingIds = ids.filter((id) => !draggedIdSet.has(String(id)));
  remainingIds.splice(Math.max(0, Math.min(targetIndex, remainingIds.length)), 0, ...draggedIds);
  return remainingIds;
};

export const applyCustomOrderToRecords = (records: ObservationRecord[], orderedIds: ObservationId[]) => {
  const nextIndexById = new Map(orderedIds.map((id, index) => [String(id), index]));
  return records.map((record) => {
    const index = nextIndexById.get(String(record.id));
    return typeof index === 'number' ? { ...record, index } : record;
  });
};

export const getCurrentOrder = (records: ObservationRecord[], visualOrder: string[] | null) => {
  const baseIds = records.map((record) => String(record.id));
  if (!visualOrder?.length) return baseIds;
  const knownIds = new Set(visualOrder);
  return [...visualOrder, ...baseIds.filter((id) => !knownIds.has(id))];
};

export const isMultiSelectModifierPressed = (event: ReactMouseEvent<HTMLElement>) => event.ctrlKey || event.metaKey;

export const getMultiSelectModifierLabel = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return 'Ctrl';
  const platformHint = (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform;
  return /mac/i.test(platformHint || navigator.platform || navigator.userAgent || '') ? 'Cmd' : 'Ctrl';
};
